import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, TextField } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CustomGrid from '../common/CustomGrid';
import Navbar from '../common/Navbar';
import { useTestingData } from './hooks/useTestingData';
import { useTestingSave } from './hooks/useTestingSave';
import { useTestingDownload } from './hooks/useTestingDownload';
import TestingTabNavigation from './components/TestingTabNavigation';
import MasterTableSheet from './components/MasterTableSheet';
import TestingDownloadModal from './components/TestingDownloadModal';
import TestingSheetStrategy from './strategies/TestingSheetStrategy';

/**
 * @author Tejas Sharma, Aditya Teltia
 * Testing Sheet Type Categories:
 *
 * 1. TAB-BASED ONLY (No individual Master Table sheets):
 *    - MAZAR (MAZAR, Template M)
 *    - AUMYAA (AUMYAA-SAP, AUMYAA-GENERIC, Template A1, Template A2)
 *    - SAIL (SAIL, Template S1)
 *    - RBI (RBI, Template R)
 *    These templates only display main tabs from RCM data.
 *    NO individual testing sheets are created per Master Table row.
 *
 * 2. WITH INDIVIDUAL SHEETS (Master Table row sheets):
 *    - BDO (Template B1, Template B2, BDO_SAP, BDO_GENERIC, BDO-*)
 *    - SOC (SOC2.0, SOC1.0, ISAE 3402)
 *    These templates create individual testing sheets for each Master Table row.
 *    Allow file upload for custom templates and use TestingSheetTab dropdown.
 */
export default function Testing() {
  const navigate = useNavigate();
  const { applicationId, engagementId } = useParams();
  const [value, setValue] = useState('1');

  // Custom hooks for data management
  const {
    RCMData,
    setRCMData,
    RCMId,
    type,
    socScope,
    gapTracker,
    applicationName,
    testingSheetType,
    dynamicTestingTabObject
  } = useTestingData(applicationId, engagementId);

  // Custom hook for save functionality
  const { handleSave, isSaving } = useTestingSave({
    RCMData,
    setRCMData,
    RCMId,
    type,
    socScope,
    gapTracker
  });

  // Custom hook for download functionality
  const {
    openDownloadModal,
    errorMessage,
    selectedFile,
    handleCloseDownload,
    handleDownload,
    handleFileSelect
  } = useTestingDownload(RCMData);

  // Local handlers
  const handleChangeRCM = (rowNumber, fieldName, cellValue, table) => {
    const modifiedRCMData = { ...RCMData };
    modifiedRCMData.rows[table][rowNumber][fieldName] = cellValue;
    setRCMData(modifiedRCMData);
  };

  const handleChangeRow = (newRow, oldRow, RCMTab) => {
    const newRCMRows = [...RCMData['rows'][RCMTab]];
    newRCMRows.forEach((element, index) => {
      if (JSON.stringify(oldRow) === JSON.stringify(element)) {
        newRCMRows[index] = newRow;
      }
    });
    const newRCMData = { ...RCMData };
    newRCMData['rows'][RCMTab] = newRCMRows;
    setRCMData(newRCMData);
  };

  // Determine if individual Master Table sheets should be rendered
  const shouldRenderMasterTableSheets =
    TestingSheetStrategy.shouldRenderMasterTableSheets(type);

  return (
    <>
      <Navbar
        pageName="TESTING"
        engagementId={engagementId}
        applicationId={applicationId}
      />

      {/* Gap Tracker Button */}
      <Box>
        {(type === 'SOC2.0' || type === 'SOC1.0' || type === 'ISAE 3402') &&
          (socScope === 'Readiness SOC1' || socScope === 'Readiness SOC2') && (
            <Button
              sx={{
                color: 'white',
                backgroundColor: 'black',
                float: 'right'
              }}
              variant="contained"
              onClick={() => navigate(`/gapTracker/gap/${gapTracker['_id']}`)}
            >
              Gap Tracker
              <ChevronRightIcon />
            </Button>
          )}
      </Box>

      <TabContext value={value}>
        {/* Tab Navigation */}
        <TestingTabNavigation
          RCMData={RCMData}
          type={type}
          testingSheetType={testingSheetType}
          value={value}
          setValue={setValue}
        />

        {/* Main Table Tabs */}
        {RCMData
          ? Object.keys(RCMData.rows).map((table, index1) => {
              return (
                <TabPanel
                  value={`${index1 + 1}`}
                  index={index1 + 1}
                  key={`Tab-data-${index1 + 1}`}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '30px'
                    }}
                  >
                    <TextField
                      name="applicationName"
                      label="Application Name"
                      disabled
                      value={applicationName}
                    />
                    <Button
                      color="success"
                      variant="contained"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                  {RCMData ? (
                    <CustomGrid
                      tabName={table}
                      rows={RCMData.rows[table]}
                      tableMeta={RCMData.meta.Testing}
                      handleChangeCB={handleChangeRow}
                      fieldsMeta={RCMData.meta.fields}
                    />
                  ) : null}
                </TabPanel>
              );
            })
          : null}

        {/* Individual Testing Sheets for Master Table Rows */}
        {RCMData &&
        shouldRenderMasterTableSheets &&
        RCMData.rows['Master Table']
          ? RCMData.rows['Master Table'].map((row, idx) => {
              return (
                <TabPanel
                  value={`${idx + 2}`}
                  index={`${idx + 2}`}
                  key={`Testing Sheet - ${idx + 2}`}
                >
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: '30px'
                    }}
                  >
                    <Button
                      color="success"
                      variant="contained"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>

                  {/* Master Table Sheet Component */}
                  <MasterTableSheet
                    rowIndex={idx}
                    rowData={row}
                    RCMData={RCMData}
                    type={type}
                    testingSheetType={testingSheetType}
                    handleChangeRCM={handleChangeRCM}
                    dynamicTestingTabObject={dynamicTestingTabObject}
                  />
                </TabPanel>
              );
            })
          : null}

        {/* Download Modal */}
        <TestingDownloadModal
          open={openDownloadModal}
          onClose={handleCloseDownload}
          onDownload={handleDownload}
          onFileSelect={handleFileSelect}
          selectedFile={selectedFile}
          errorMessage={errorMessage}
        />
      </TabContext>
    </>
  );
}
